/**
 * TranscriptViewer — Reusable component for displaying transcribed workshop comments.
 * Shows summary + highlighted quote cards collapsed, expandable to full chat timeline.
 */
import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { MessageSquare, Users, Quote, ChevronDown, ChevronUp } from 'lucide-react';
import EditableText from './EditableText';
import TranslatedText from './TranslatedText';
import { useTranslation } from '../../hooks/useTranslation.jsx';

// ============================================================================
// SPEAKER COLOR PALETTE
// ============================================================================

const SPEAKER_COLORS = [
  { bg: '#f5f5f5', accent: '#367ABA', avatar: '#367ABA', text: '#2c5e91' },  // blue
  { bg: '#d1fae5', accent: '#00613d', avatar: '#00613d', text: '#065f46' },  // emerald
  { bg: 'rgba(255, 217, 61, 0.25)', accent: '#fa6129', avatar: '#fa6129', text: '#92400e' },  // amber
  { bg: '#ede9fe', accent: '#8b5cf6', avatar: '#7c3aed', text: '#5b21b6' },  // violet
  { bg: '#fee2e2', accent: '#fa6129', avatar: '#fa6129', text: '#a83d10' },  // red
  { bg: '#cffafe', accent: '#06b6d4', avatar: '#0891b2', text: '#155e75' },  // cyan
  { bg: '#fce7f3', accent: '#ec4899', avatar: '#db2777', text: '#9d174d' },  // pink
  { bg: '#ccfbf1', accent: '#14b8a6', avatar: '#0d9488', text: '#134e4a' },  // teal
];

const SPEAKER_COLORS_DARK = [
  { bg: 'rgba(59,130,246,0.15)', accent: '#60a5fa', avatar: '#367ABA', text: '#93c5fd' },
  { bg: 'rgba(16,185,129,0.15)', accent: '#34d399', avatar: '#00613d', text: '#6ee7b7' },
  { bg: 'rgba(245,158,11,0.15)', accent: '#fbbf24', avatar: '#fa6129', text: '#fcd34d' },
  { bg: 'rgba(139,92,246,0.15)', accent: '#a78bfa', avatar: '#8b5cf6', text: '#c4b5fd' },
  { bg: 'rgba(239,68,68,0.15)', accent: '#f87171', avatar: '#fa6129', text: '#fca5a5' },
  { bg: 'rgba(6,182,212,0.15)', accent: '#22d3ee', avatar: '#06b6d4', text: '#67e8f9' },
  { bg: 'rgba(236,72,153,0.15)', accent: '#f472b6', avatar: '#ec4899', text: '#f9a8d4' },
  { bg: 'rgba(20,184,166,0.15)', accent: '#2dd4bf', avatar: '#14b8a6', text: '#5eead4' },
];

// ============================================================================
// THEME TOKENS
// ============================================================================

const THEMES = {
  light: {
    cardBg: '#fff',
    cardBorder: '#e5e7eb',
    textPrimary: '#000',
    textSecondary: '#6b7280',
    quoteBg: '#f9fafb',
    quoteAccent: '#367ABA',
    timelineBg: '#f9fafb',
    timelineLine: '#e5e7eb',
    expandColor: '#367ABA',
    expandHover: '#2c5e91',
    badgeBg: '#f3f4f6',
    badgeText: '#6b7280',
    divider: '#e5e7eb',
    emptyText: '#9ca3af',
  },
  dark: {
    cardBg: '#1e1e30',
    cardBorder: '#2d2d44',
    textPrimary: '#e5e7eb',
    textSecondary: '#9ca3af',
    quoteBg: 'rgba(255,255,255,0.04)',
    quoteAccent: '#60a5fa',
    timelineBg: 'rgba(0,0,0,0.2)',
    timelineLine: '#374151',
    expandColor: '#60a5fa',
    expandHover: '#93c5fd',
    badgeBg: 'rgba(255,255,255,0.08)',
    badgeText: '#9ca3af',
    divider: '#374151',
    emptyText: '#6b7280',
  },
};

// ============================================================================
// ANIMATIONS
// ============================================================================

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Wrapper = styled.div`
  background: ${p => p.$t.cardBg};
  border: 1px solid ${p => p.$t.cardBorder};
  border-radius: 0;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 24px 0;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.$t.textPrimary};
  margin: 0;
  flex: 1;
`;

const Badge = styled.span`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 0;
  background: ${p => p.$t.badgeBg};
  color: ${p => p.$t.badgeText};
  white-space: nowrap;
`;

const Summary = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: ${p => p.$t.textSecondary};
  padding: 12px 24px 0;
  margin: 0;
`;

const HighlightsArea = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const QuoteCard = styled.div`
  background: ${p => p.$t.quoteBg};
  border-left: 3px solid ${p => p.$accent};
  border-radius: 0;
  padding: 12px 16px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const QuoteIcon = styled.div`
  color: ${p => p.$accent};
  flex-shrink: 0;
  margin-top: 2px;
`;

const QuoteBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuoteText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.$t.textPrimary};
  margin: 0;
  font-style: italic;
`;

const QuoteAttr = styled.span`
  font-size: 12px;
  color: ${p => p.$t.textSecondary};
  margin-top: 4px;
  display: block;
`;

const QuoteLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.$accent};
  margin-bottom: 4px;
  text-shadow: ${p => isLowContrast(p.$accent) ? '0 0 0 #000' : 'none'};
  filter: ${p => isLowContrast(p.$accent) ? 'brightness(0.65)' : 'none'};
`;

const ShowMoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${p => p.$t.expandColor};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.$t.expandColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  padding: 12px 24px;
  width: 100%;
  border-top: 1px solid ${p => p.$t.divider};

  &:hover {
    color: ${p => p.$t.expandHover};
  }
`;

const TimelineArea = styled.div`
  animation: ${fadeSlideIn} 0.3s ease;
  border-top: 1px solid ${p => p.$t.divider};
  background: ${p => p.$t.timelineBg};
  max-height: ${p => p.$maxHeight};
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* thin scrollbar */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.$t.timelineLine};
    border-radius: 3px;
  }
`;

const CommentRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => p.$color};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const Bubble = styled.div`
  background: ${p => p.$bg};
  border-radius: 0;
  padding: 10px 14px;
  flex: 1;
  min-width: 0;
`;

const BubbleHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
`;

const SpeakerName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$color};
`;

const Timestamp = styled.span`
  font-size: 11px;
  color: ${p => p.$t.textSecondary};
`;

const BubbleText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.$t.textPrimary};
  margin: 0;
`;

const EmptyState = styled.div`
  padding: 32px 24px;
  text-align: center;
  color: ${p => p.$t.emptyText};
  font-size: 14px;
`;

// ============================================================================
// HELPERS
// ============================================================================

function isLowContrast(hex) {
  if (!hex) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Relative luminance — high values = light colors = low contrast on white
  return (0.299 * r + 0.587 * g + 0.114 * b) > 180;
}

function formatTime(seconds) {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function pickHighlights(comments, count, strategy) {
  if (!comments.length) return [];
  if (comments.length <= count) return comments;

  switch (strategy) {
    case 'first':
      return comments.slice(0, count);
    case 'random': {
      const shuffled = [...comments].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, count);
    }
    case 'longest':
    default: {
      const sorted = [...comments].sort((a, b) => (b.text?.length || 0) - (a.text?.length || 0));
      return sorted.slice(0, count);
    }
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function TranscriptViewer({
  comments = [],
  title = 'Litterointi',
  summary = null,
  highlightCount = 3,
  highlightPicker = 'longest',
  highlightLabels,
  theme = 'light',
  maxHeight = '600px',
  className,
}) {
  const { t: i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [expandedQuotes, setExpandedQuotes] = useState(new Set());
  const t = THEMES[theme] || THEMES.light;
  const isDark = theme === 'dark';
  const palette = isDark ? SPEAKER_COLORS_DARK : SPEAKER_COLORS;

  // Assign colors by order of first appearance
  const speakerColorMap = useMemo(() => {
    const map = {};
    let idx = 0;
    for (const c of comments) {
      const key = c.speaker;
      if (key && !(key in map)) {
        map[key] = palette[idx % palette.length];
        idx++;
      }
    }
    return map;
  }, [comments, palette]);

  const speakerCount = Object.keys(speakerColorMap).length;
  const highlights = useMemo(() => {
    // When labels carry matchKeys (e.g. scenario title + tulevaisuuskuva),
    // pick the longest quote that mentions each label's keywords. Time order
    // is unreliable because groups discuss scenarios out of list order.
    if (highlightLabels?.[0]?.matchKeys) {
      const used = new Set();
      const matched = [];
      for (const label of highlightLabels) {
        const candidate = comments
          .filter(c => !used.has(c.id) && label.matchKeys.some(k => c.text?.toLowerCase().includes(k)))
          .sort((a, b) => (b.text?.length || 0) - (a.text?.length || 0))[0];
        if (candidate) {
          used.add(candidate.id);
          matched.push({ ...candidate, _label: label });
        }
      }
      return matched;
    }
    return pickHighlights(comments, highlightCount, highlightPicker);
  }, [comments, highlightCount, highlightPicker, highlightLabels]);

  const displayName = (c) => c.label || c.speaker || '?';
  const initial = (c) => (displayName(c))[0]?.toUpperCase() || '?';

  if (!comments.length) {
    return (
      <Wrapper $t={t} className={className}>
        <Header $t={t}>
          <MessageSquare size={20} color={t.textSecondary} />
          <Title $t={t}>{title}</Title>
        </Header>
        <EmptyState $t={t}>{i18n('report.transcript.emptyComments')}</EmptyState>
      </Wrapper>
    );
  }

  return (
    <Wrapper $t={t} className={className}>
      {/* Header */}
      <Header>
        <MessageSquare size={20} color={t.expandColor} />
        <Title $t={t}>{title}</Title>
        <Badge $t={t}>
          <Users size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
          {i18n('report.transcript.speakersComments', { speakers: speakerCount, comments: comments.length })}
        </Badge>
      </Header>

      {/* Optional summary */}
      {summary && (
        <Summary $t={t}>
          <EditableText fieldKey="transcript.summary" fallback={summary} label="Vaiheen tiivistelmä" />
        </Summary>
      )}

      {/* Highlights */}
      <HighlightsArea>
        {highlights.map((c, idx) => {
          const color = speakerColorMap[c.speaker] || palette[0];
          const hasSummary = c.summary && c.summary !== c.text;
          const isQuoteExpanded = expandedQuotes.has(c.id);
          const displayText = hasSummary && !isQuoteExpanded ? c.summary : c.text;
          const label = c._label || highlightLabels?.[idx];
          const cardAccent = label?.color || color.accent;

          return (
            <QuoteCard key={c.id} $t={t} $accent={cardAccent}>
              <QuoteIcon $accent={cardAccent}><Quote size={16} /></QuoteIcon>
              <QuoteBody>
                {label && <QuoteLabel $accent={cardAccent}>{label.text || label}</QuoteLabel>}
                <QuoteText $t={t}>
                  <TranslatedText
                    fieldKey={`transcript.${c.id}.${isQuoteExpanded || !hasSummary ? 'text' : 'summary'}`}
                    fallback={displayText}
                    label={isQuoteExpanded || !hasSummary ? 'Puheenvuoro' : 'Puheenvuoron tiivistelmä'}
                  />
                </QuoteText>
                <QuoteAttr $t={t}>— {displayName(c)}{c.startTime != null ? ` (${formatTime(c.startTime)})` : ''}</QuoteAttr>
                {hasSummary && (
                  <ShowMoreLink $t={t} onClick={() => setExpandedQuotes(prev => {
                    const next = new Set(prev);
                    if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                    return next;
                  })}>
                    {isQuoteExpanded ? i18n('report.transcript.showSummary') : i18n('report.transcript.showFullQuote')}
                  </ShowMoreLink>
                )}
              </QuoteBody>
            </QuoteCard>
          );
        })}
      </HighlightsArea>

      {/* Expand / collapse */}
      <ExpandButton $t={t} onClick={() => setExpanded(v => !v)}>
        {expanded ? (
          <>{i18n('report.transcript.hideTimeline')} <ChevronUp size={16} /></>
        ) : (
          <>{i18n('report.transcript.showAllComments', { count: comments.length })} <ChevronDown size={16} /></>
        )}
      </ExpandButton>

      {/* Timeline */}
      {expanded && (
        <TimelineArea $t={t} $maxHeight={maxHeight}>
          {comments.map((c) => {
            const color = speakerColorMap[c.speaker] || palette[0];
            return (
              <CommentRow key={c.id}>
                <Avatar $color={color.avatar}>{initial(c)}</Avatar>
                <Bubble $bg={color.bg}>
                  <BubbleHeader>
                    <SpeakerName $color={color.text}>{displayName(c)}</SpeakerName>
                    {c.startTime != null && <Timestamp $t={t}>{formatTime(c.startTime)}</Timestamp>}
                  </BubbleHeader>
                  <BubbleText $t={t}>
                    <TranslatedText fieldKey={`transcript.${c.id}.text`} fallback={c.text} label="Puheenvuoro" />
                  </BubbleText>
                </Bubble>
              </CommentRow>
            );
          })}
        </TimelineArea>
      )}
    </Wrapper>
  );
}
